/**
 * SMS-based multi-factor auth helpers (Firebase Auth + Identity Platform MFA).
 * Requires Phone provider + MFA enabled in Firebase Console — see docs/MFA_SMS_SETUP.md
 */
import {
  EmailAuthProvider,
  getMultiFactorResolver,
  multiFactor,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  RecaptchaVerifier,
  reauthenticateWithCredential,
} from 'firebase/auth';

/** Refresh session so MFA enrollment APIs accept the user (Firebase: auth/requires-recent-login). */
export async function reauthenticateWithEmailPassword(auth, password) {
  const user = auth.currentUser;
  if (!user?.email) {
    const err = new Error(
      'This account cannot use password confirmation. Sign out and sign in again, then try SMS MFA.'
    );
    err.code = 'auth/no-email-password';
    throw err;
  }
  const trimmed = (password || '').trim();
  if (!trimmed) {
    const err = new Error('Enter your account password.');
    err.code = 'auth/missing-password';
    throw err;
  }
  await reauthenticateWithCredential(user, EmailAuthProvider.credential(user.email, trimmed));
}

export function pickPhoneMfaHint(resolver) {
  return resolver.hints.find((h) => h.factorId === PhoneMultiFactorGenerator.FACTOR_ID);
}

/**
 * Start SMS challenge after sign-in threw auth/multi-factor-auth-required.
 * @returns {{ resolver, verifier, verificationId }}
 */
export async function beginSmsSignInChallenge(auth, multiFactorError, recaptchaContainerId) {
  const resolver = getMultiFactorResolver(auth, multiFactorError);
  const hint = pickPhoneMfaHint(resolver);
  if (!hint) {
    const err = new Error('No SMS second factor is registered for this account.');
    err.code = 'auth/no-phone-mfa-hint';
    throw err;
  }
  const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'invisible' });
  const phoneProvider = new PhoneAuthProvider(auth);
  const verificationId = await phoneProvider.verifyPhoneNumber(
    {
      multiFactorHint: hint,
      session: resolver.session,
    },
    verifier
  );
  return { resolver, hint, verifier, verificationId };
}

/** Send another SMS for an in-progress MFA sign-in (e.g. wrong code or timeout). */
export async function resendSmsSignInChallenge(auth, resolver, hint, recaptchaContainerId, previousVerifier) {
  if (previousVerifier) {
    try {
      previousVerifier.clear();
    } catch (_) {
      /* ignore */
    }
  }
  const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'invisible' });
  const phoneProvider = new PhoneAuthProvider(auth);
  const verificationId = await phoneProvider.verifyPhoneNumber(
    {
      multiFactorHint: hint,
      session: resolver.session,
    },
    verifier
  );
  return { verifier, verificationId };
}

export async function completeSmsSignIn(resolver, verifier, verificationId, smsCode) {
  try {
    const cred = PhoneAuthProvider.credential(verificationId, smsCode.trim());
    const assertion = PhoneMultiFactorGenerator.assertion(cred);
    await resolver.resolveSignIn(assertion);
  } finally {
    try {
      verifier.clear();
    } catch (_) {
      /* ignore */
    }
  }
}

export async function beginSmsEnrollment(auth, phoneNumberE164, recaptchaContainerId) {
  const user = auth.currentUser;
  if (!user) {
    const err = new Error('You must be signed in to enroll MFA.');
    err.code = 'auth/not-signed-in';
    throw err;
  }
  const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, { size: 'invisible' });
  const session = await multiFactor(user).getSession();
  const phoneProvider = new PhoneAuthProvider(auth);
  const verificationId = await phoneProvider.verifyPhoneNumber(
    {
      phoneNumber: phoneNumberE164,
      session,
    },
    verifier
  );
  return { verifier, verificationId };
}

export async function finishSmsEnrollment(auth, verifier, verificationId, smsCode, displayName = 'SMS') {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('Not signed in');
    const cred = PhoneAuthProvider.credential(verificationId, smsCode.trim());
    const assertion = PhoneMultiFactorGenerator.assertion(cred);
    await multiFactor(user).enroll(assertion, displayName);
  } finally {
    verifier.clear();
  }
}

export async function unenrollSmsFactor(auth, factor) {
  const user = auth.currentUser;
  if (!user) throw new Error('Not signed in');
  await multiFactor(user).unenroll(factor);
}
